import { Link } from "react-router-dom";
import { useState } from "react";
import { Instagram, Facebook, Mail, Phone, MapPin, Clock, ArrowRight } from "lucide-react";
import { toast } from "sonner";

const DIVISIONS = [
  { to: "/shop", label: "Shop All Products" },
  { to: "/shop", label: "Custom Apparel" },
  { to: "/shop", label: "Tumblers & Drinkware" },
  { to: "/shop/studio", label: "Custom Order Studio" },
];
const QUICK = [
  { to: "/", label: "Home" },
  { to: "/shop", label: "Shop" },
  { to: "/about", label: "About" },
  { to: "/contact", label: "Contact" },
  { to: "/account", label: "My Account" },
];

export default function Footer() {
  const year = new Date().getFullYear();
  const [email, setEmail] = useState("");
  const subscribe = (e) => {
    e.preventDefault();
    if (!email) return;
    toast.success("You're on the list! We'll be in touch soon.");
    setEmail("");
  };
  return (
    <footer className="relative bg-black text-chrome/70 border-t border-electric/15 overflow-hidden" data-testid="site-footer">
      {/* Newsletter band */}
      <div className="border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 py-12 flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="text-center md:text-left">
            <h3 className="font-serif text-2xl sm:text-3xl text-white">Join the Collins Crafty list</h3>
            <p className="text-sm text-chrome/55 mt-1">New drops, custom-order tips & members-only offers — straight to your inbox.</p>
          </div>
          <form onSubmit={subscribe} className="flex w-full max-w-md gap-2" data-testid="newsletter-form">
            <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Your email address"
              className="flex-1 bg-white/5 border border-white/15 rounded-lg px-4 py-3 text-sm text-white placeholder:text-chrome/40 focus:outline-none focus:border-electric transition-colors" data-testid="newsletter-email" />
            <button type="submit" className="inline-flex items-center gap-2 bg-electric text-white uppercase tracking-[0.15em] text-[11px] font-bold px-6 py-3 rounded-lg hover:bg-royal transition-colors glow-blue-sm" data-testid="newsletter-submit">
              Subscribe <ArrowRight size={13} />
            </button>
          </form>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-16 grid gap-12 md:grid-cols-2 lg:grid-cols-4">
        {/* Brand & Mission */}
        <div>
          <div className="flex items-center gap-3">
            <span className="relative inline-flex items-baseline metal-text font-serif leading-none drop-shadow-[0_0_10px_rgba(0,140,255,0.25)]" aria-hidden="true">
              <span className="text-4xl">C</span><span className="text-4xl -ml-2.5">C</span>
            </span>
            <span className="font-serif text-white text-xl leading-tight">Collins Crafty<br /><span className="text-chrome/60 text-[10px] tracking-[0.3em]">CREATIONS</span></span>
          </div>
          <div className="font-script text-2xl blue-text mt-4">Handmade with heart.</div>
          <p className="text-sm mt-3 leading-relaxed text-chrome/55">
            Custom apparel, tumblers, personalized gifts & bespoke printing — handcrafted in Memphis for every occasion.
          </p>
          <div className="flex items-center gap-3 mt-5">
            <a href="#" aria-label="Facebook" data-testid="footer-facebook" className="w-9 h-9 rounded-full border border-white/15 flex items-center justify-center hover:border-electric hover:text-electric transition-colors"><Facebook size={15} /></a>
            <a href="#" aria-label="Instagram" data-testid="footer-instagram" className="w-9 h-9 rounded-full border border-white/15 flex items-center justify-center hover:border-electric hover:text-electric transition-colors"><Instagram size={15} /></a>
            <a href="mailto:hello@collinscrafty.co" aria-label="Email" data-testid="footer-email" className="w-9 h-9 rounded-full border border-white/15 flex items-center justify-center hover:border-electric hover:text-electric transition-colors"><Mail size={15} /></a>
          </div>
        </div>

        {/* Shop */}
        <div>
          <h4 className="font-serif text-white text-lg">Shop</h4>
          <span className="block h-px w-10 bg-electric mt-3 mb-4 shadow-[0_0_8px_rgba(0,140,255,0.6)]" />
          <ul className="space-y-2.5 text-sm">
            {DIVISIONS.map((d) => (
              <li key={d.label}><Link to={d.to} className="hover:text-electric transition-colors" data-testid={`footer-div-${d.label}`}>{d.label}</Link></li>
            ))}
          </ul>
        </div>

        {/* Quick Links */}
        <div>
          <h4 className="font-serif text-white text-lg">Quick Links</h4>
          <span className="block h-px w-10 bg-electric mt-3 mb-4 shadow-[0_0_8px_rgba(0,140,255,0.6)]" />
          <ul className="space-y-2.5 text-sm">
            {QUICK.map((q) => (
              <li key={q.to}><Link to={q.to} className="hover:text-electric transition-colors" data-testid={`footer-link-${q.to}`}>{q.label}</Link></li>
            ))}
          </ul>
        </div>

        {/* Contact & Hours */}
        <div>
          <h4 className="font-serif text-white text-lg">Contact &amp; Hours</h4>
          <span className="block h-px w-10 bg-electric mt-3 mb-4 shadow-[0_0_8px_rgba(0,140,255,0.6)]" />
          <ul className="space-y-3 text-sm">
            <li className="flex items-start gap-2.5"><MapPin size={15} className="text-electric mt-0.5 shrink-0" /> Memphis &amp; Surrounding Areas, TN</li>
            <li className="flex items-center gap-2.5"><Mail size={15} className="text-electric shrink-0" /> <a href="mailto:hello@collinscrafty.co" className="hover:text-electric transition-colors">hello@collinscrafty.co</a></li>
            <li className="flex items-center gap-2.5"><Phone size={15} className="text-electric shrink-0" /> <a href="tel:+19015550100" className="hover:text-electric transition-colors">(901) 555-0100</a></li>
            <li className="flex items-start gap-2.5"><Clock size={15} className="text-electric mt-0.5 shrink-0" /> Mon–Sat, 9:00 AM – 6:00 PM CT</li>
          </ul>
        </div>
      </div>

      {/* Compliance strip */}
      <div className="border-t border-white/10">
        <div className="max-w-7xl mx-auto px-6 py-6 text-[11px] leading-relaxed text-chrome/40 space-y-1">
          <p>All products are handmade to order; slight variations are part of the handcrafted charm. Custom orders require approval and payment before production begins.</p>
          <p>Design fees may apply to custom artwork and will be communicated before any work begins. Turnaround times vary by product and order volume.</p>
        </div>
      </div>

      {/* Bottom bar */}
      <div className="border-t border-white/10">
        <div className="max-w-7xl mx-auto px-6 py-5 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-chrome/50">
          <div>© {year} Collins Crafty Creations. All rights reserved.</div>
          <div className="flex items-center gap-3">
            <Link to="/privacy" className="hover:text-electric transition-colors">Privacy Policy</Link>
            <span className="text-white/20">|</span>
            <Link to="/terms" className="hover:text-electric transition-colors">Terms of Service</Link>
            <span className="text-white/20">|</span>
            <Link to="/admin" className="text-chrome/30 hover:text-electric transition-colors" data-testid="footer-admin">Admin Portal</Link>
          </div>
        </div>
      </div>
      {/* Giant faint wordmark watermark */}
      <div className="relative select-none pointer-events-none" aria-hidden="true">
        <div className="max-w-7xl mx-auto px-6">
          <div className="font-serif uppercase tracking-tight text-white/[0.04] leading-none text-[18vw] md:text-[13vw] text-center whitespace-nowrap overflow-hidden">Collins Crafty</div>
        </div>
      </div>

      {/* Mobile bottom-bar spacer */}
      <div className="h-16 md:hidden" />
    </footer>
  );
}
